import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type AuditLogEntry } from "../lib/api.ts";

// Admin-only forensic view of every /v1/staff/* mutation. Cursor-paginated
// by descending timestamp because the table grows monotonically and a
// fixed-offset paging would skip entries written between page loads.

type Filters = {
  actor:      string;
  targetType: string;
  targetId:   string;
  success:    "" | "true" | "false";
  sinceHours: number | null;  // null = no lower bound
};

const DEFAULT_FILTERS: Filters = {
  actor:      "",
  targetType: "",
  targetId:   "",
  success:    "",
  sinceHours: 24,
};

const PAGE_SIZE = 100;

export function StaffAuditLogPage() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [staged, setStaged]   = useState<Filters>(DEFAULT_FILTERS);
  const [cursor, setCursor]   = useState<number | null>(null);
  // History of cursor values so the Back button can step through pages
  // without reissuing the same query. Each entry is the `before` we used
  // for that page; cursor=null means "first page".
  const [history, setHistory] = useState<Array<number | null>>([null]);

  const sinceMs = filters.sinceHours
    ? Date.now() - filters.sinceHours * 3600 * 1000
    : undefined;

  const q = useQuery({
    queryKey: ["staff", "audit-log", filters, cursor],
    queryFn: () => api.staffAuditLog({
      limit:      PAGE_SIZE,
      before:     cursor ?? undefined,
      sinceMs,
      actor:      filters.actor      || undefined,
      targetType: filters.targetType || undefined,
      targetId:   filters.targetId   || undefined,
      success:    filters.success === ""
                    ? undefined
                    : filters.success === "true",
    }),
    placeholderData: (prev) => prev,
  });

  const entries = q.data?.entries ?? [];
  const hasMore = q.data?.hasMore ?? false;

  const applyFilters = () => {
    setFilters(staged);
    setCursor(null);
    setHistory([null]);
  };
  const resetFilters = () => {
    setStaged(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
    setCursor(null);
    setHistory([null]);
  };
  const goNext = () => {
    if (!q.data?.nextBefore) return;
    setHistory((h) => [...h, q.data!.nextBefore]);
    setCursor(q.data!.nextBefore);
  };
  const goPrev = () => {
    if (history.length <= 1) return;
    const trimmed = history.slice(0, -1);
    setHistory(trimmed);
    setCursor(trimmed[trimmed.length - 1] ?? null);
  };

  return (
    <div>
      <h2>Audit log</h2>
      <p className="muted" style={{ marginTop: "-0.5rem" }}>
        Every <code>/v1/staff/*</code> mutation since 2026-05-15. cf-ray
        cross-references Cloudflare Logpush for the raw edge request.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "0.5rem",
          margin: "1rem 0",
          padding: "0.75rem",
          background: "rgba(148,163,184,0.08)",
          borderRadius: "6px",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85em" }}>
          Actor email
          <input
            placeholder="ryp@…"
            value={staged.actor}
            onChange={(e) => setStaged({ ...staged, actor: e.target.value })}
          />
          <span className="muted" style={{ fontSize: "0.75em" }}>
            Matches user_id, not email — paste from a previous row.
          </span>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85em" }}>
          Target type
          <select
            value={staged.targetType}
            onChange={(e) => setStaged({ ...staged, targetType: e.target.value })}
          >
            <option value="">any</option>
            <option value="user">user</option>
            <option value="org">org</option>
            <option value="oauth_client">oauth_client</option>
            <option value="release">release</option>
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85em" }}>
          Target id
          <input
            placeholder="usr_… / org id / release filename"
            value={staged.targetId}
            onChange={(e) => setStaged({ ...staged, targetId: e.target.value })}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85em" }}>
          Result
          <select
            value={staged.success}
            onChange={(e) =>
              setStaged({ ...staged, success: e.target.value as Filters["success"] })}
          >
            <option value="">any</option>
            <option value="true">success</option>
            <option value="false">failure</option>
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", fontSize: "0.85em" }}>
          Since (hours)
          <select
            value={String(staged.sinceHours ?? "")}
            onChange={(e) =>
              setStaged({
                ...staged,
                sinceHours: e.target.value === "" ? null : parseInt(e.target.value, 10),
              })}
          >
            <option value="1">last hour</option>
            <option value="24">last 24 h</option>
            <option value="168">last 7 days</option>
            <option value="720">last 30 days</option>
            <option value="">all time</option>
          </select>
        </label>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem" }}>
          <button type="submit">Apply</button>
          <button type="button" className="secondary" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </form>

      {q.isError && (
        <p className="err">Couldn't load — {(q.error as Error).message}</p>
      )}

      <div style={{ overflowX: "auto" }}>
        <table className="rows">
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>When (UTC)</th>
              <th>Actor</th>
              <th>Role</th>
              <th>Action</th>
              <th>Target</th>
              <th>Details</th>
              <th>OK?</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !q.isFetching && (
              <tr><td colSpan={7} className="muted" style={{ padding: "1rem" }}>
                No audit entries match those filters.
              </td></tr>
            )}
            {entries.map((e) => <AuditRow key={e.id} entry={e} />)}
          </tbody>
        </table>
      </div>

      <div style={{
        display: "flex", gap: "0.5rem", marginTop: "1rem",
        alignItems: "center", justifyContent: "space-between",
      }}>
        <div className="muted" style={{ fontSize: "0.85em" }}>
          {q.isFetching ? "Loading…" : `Showing ${entries.length} entr${entries.length === 1 ? "y" : "ies"}`}
          {history.length > 1 && ` · page ${history.length}`}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            className="secondary"
            onClick={goPrev}
            disabled={history.length <= 1 || q.isFetching}
          >
            ← Newer
          </button>
          <button
            onClick={goNext}
            disabled={!hasMore || q.isFetching}
          >
            Older →
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const at = new Date(entry.timestamp);
  // Y-m-d H:M:S in UTC. The audit-log query string also accepts ISO times
  // if we ever want to filter by exact moment.
  const atStr =
    `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())} ` +
    `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}:${pad(at.getUTCSeconds())}`;

  const targetSummaryStr = entry.targetSummary
    ? JSON.stringify(entry.targetSummary, null, 2)
    : null;

  return (
    <>
      <tr style={{ cursor: "pointer" }} onClick={() => setExpanded((v) => !v)}>
        <td style={{ fontFamily: "monospace", fontSize: "0.85em", whiteSpace: "nowrap" }}>{atStr}</td>
        <td>{entry.actorEmail}</td>
        <td>{entry.staffRole}</td>
        <td><code>{entry.action}</code></td>
        <td>
          {entry.targetType && entry.targetId ? (
            <span style={{ fontFamily: "monospace", fontSize: "0.85em" }}>
              {entry.targetType}/{entry.targetId}
            </span>
          ) : entry.targetType ? (
            <span className="muted">{entry.targetType}</span>
          ) : (
            <span className="muted">—</span>
          )}
        </td>
        <td>
          {targetSummaryStr
            ? <span className="muted" style={{ fontSize: "0.85em" }}>{expanded ? "▾" : "▸"} json</span>
            : entry.failureReason
              ? <span className="err" style={{ fontSize: "0.85em" }}>{entry.failureReason.slice(0, 60)}</span>
              : <span className="muted">—</span>}
        </td>
        <td>{entry.success ? "✓" : "✗"}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ background: "rgba(148,163,184,0.05)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "min-content 1fr", gap: "0.25rem 1rem", fontSize: "0.85em", padding: "0.5rem 1rem" }}>
              <span className="muted">cf-ray</span><code>{entry.requestId ?? "—"}</code>
              <span className="muted">IP</span><code>{entry.ip ?? "—"}</code>
              <span className="muted">UA</span><code style={{ wordBreak: "break-all" }}>{entry.userAgent ?? "—"}</code>
              <span className="muted">actor_id</span><code>{entry.actorUserId}</code>
              {targetSummaryStr && (
                <>
                  <span className="muted">summary</span>
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{targetSummaryStr}</pre>
                </>
              )}
              {entry.failureReason && (
                <>
                  <span className="muted">failure</span>
                  <pre className="err" style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                    {entry.failureReason}
                  </pre>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}
